"use client";

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { EASE_SMOOTH, DURATION_NORMAL } from "@/components/motion/constants";

interface KineticTextProps {
  text: string;
  as?: "span" | "p" | "h1" | "h2" | "h3";
  className?: string;
  /** Animate per word (default) or per character */
  mode?: "words" | "chars";
  delay?: number;
  stagger?: number;
  once?: boolean;
  /** "inView" animates when scrolled into view; "mount" animates on first paint */
  trigger?: "inView" | "mount";
}

export function KineticText({
  text,
  as: Tag = "span",
  className,
  mode = "words",
  delay = 0,
  stagger = 0.04,
  once = true,
  trigger = "inView",
}: KineticTextProps) {
  const reduced = useReducedMotion();
  const units = mode === "words" ? text.split(" ") : text.split("");

  if (reduced) {
    return <Tag className={className}>{text}</Tag>;
  }

  const motionProps =
    trigger === "mount"
      ? {
          initial: { opacity: 0, y: "110%" as const },
          animate: { opacity: 1, y: 0 },
        }
      : {
          initial: { opacity: 0, y: "110%" as const },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once, amount: 0.15 },
        };

  return (
    <Tag className={cn("inline", className)} aria-label={text}>
      {units.map((unit, i) => (
        <span key={`${unit}-${i}`} className="inline-block overflow-hidden">
          <motion.span
            className="inline-block"
            {...motionProps}
            transition={{
              duration: DURATION_NORMAL,
              ease: EASE_SMOOTH,
              delay: delay + i * stagger,
            }}
          >
            {unit}
            {mode === "words" && i < units.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

interface KineticEyebrowProps {
  children: string;
  className?: string;
}

export function KineticEyebrow({ children, className }: KineticEyebrowProps) {
  return (
    <p
      className={cn(
        "text-xs font-semibold uppercase tracking-[0.25em] text-primary",
        className
      )}
    >
      <KineticText text={children} mode="chars" stagger={0.03} delay={0.05} />
    </p>
  );
}

interface KineticHeadlineProps {
  lines: { text: string; className?: string }[];
  className?: string;
}

export function KineticHeadline({ lines, className }: KineticHeadlineProps) {
  const reduced = useReducedMotion();

  return (
    <h1 className={cn(className)}>
      {lines.map((line, lineIndex) => (
        <span key={lineIndex} className="block overflow-hidden pb-1">
          {reduced ? (
            <span className={cn("block", line.className)}>{line.text}</span>
          ) : (
            <motion.span
              className={cn("block", line.className)}
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.7,
                ease: EASE_SMOOTH,
                delay: 0.12 + lineIndex * 0.14,
              }}
            >
              {line.text}
            </motion.span>
          )}
        </span>
      ))}
    </h1>
  );
}
